import {useMutation} from '@apollo/client'
import React, {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  ADD_USER_DEDUCTION_TYPE,
  EDIT_USER_DEDUCTION_TYPE,
} from '../../../gql/mutations/userDeductionType'
import {
  IAllCommisionDeductionTypesNode,
  IResCreateCommisionDeductionTypes,
  IResUpdateCommisionDeductionTypes,
} from '../../../types/users'
import {parseId} from '../../../helpers'
import {DEDUCTION_TYPE_OPTIONS} from '../../../helpers/consts'

interface IModalAddUserDeductionTypeProps {
  keyboard: boolean
  backdrop: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  isEdit?: boolean
  commisionDeductionType?: IAllCommisionDeductionTypesNode | undefined
}

export const ModalAddUserDeductionType: React.FC<IModalAddUserDeductionTypeProps> = ({
  keyboard,
  backdrop,
  isOpen,
  onClose,
  refetch,
  isEdit = false,
  commisionDeductionType,
}) => {
  // const [gqlUpdate, {data, loading, error}] = useMutation<IResAddTag>(ADD_USER_DEDUCTION_TYPE)

  const [gqlCreate, {data: dataCreate, loading: loadingCreate, error: errorCreate}] =
    useMutation<IResCreateCommisionDeductionTypes>(ADD_USER_DEDUCTION_TYPE)

  const [gqlUpdate, {data: dataUpdate}] =
    useMutation<IResUpdateCommisionDeductionTypes>(EDIT_USER_DEDUCTION_TYPE)

  useEffect(() => {
    if (errorCreate) toast.error(`Error ${errorCreate.message}`)
  }, [errorCreate])

  useEffect(() => {
    if (!dataCreate) return

    if (
      dataCreate.CommissionDeductionTypeCreate.success &&
      dataCreate.CommissionDeductionTypeCreate.commissionDeductionType?.id
    ) {
      toast.success('Commission rate updated successfully')
      onClose()
      refetch()
    } else if (
      dataCreate.CommissionDeductionTypeCreate.errors &&
      dataCreate.CommissionDeductionTypeCreate.errors.length > 0
    ) {
      toast.error(dataCreate.CommissionDeductionTypeCreate.errors[0]?.message || 'Created failed')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate.CommissionDeductionTypeUpdate.success) {
      toast.success('Commission rate updated successfully')
      onClose()
      refetch()
    } else if (
      dataUpdate.CommissionDeductionTypeUpdate.errors &&
      dataUpdate.CommissionDeductionTypeUpdate.errors.length > 0
    ) {
      toast.error(dataUpdate.CommissionDeductionTypeUpdate.errors[0]?.message || 'Update failed')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const {name = '', deductionType = '', value = ''} = commisionDeductionType || {}

  const [formData, setFormData] = useState({
    name: isEdit ? name : '',
    deductionType: isEdit ? deductionType : '',
    value: isEdit ? value : '',
  })

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({...prev, [field]: value}))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (commisionDeductionType && commisionDeductionType.id) {
      gqlUpdate({
        variables: {
          id: parseId(commisionDeductionType.id),
          input: {
            name: formData.name,
            deductionType: formData.deductionType || null,
            value: '' + parseFloat(formData.value),
          },
        },
      })
    } else {
      gqlCreate({
        variables: {
          input: {
            name: formData.name,
            deductionType: '' + formData.deductionType || null,
            value: '' + parseFloat(formData.value),
          },
        },
      })
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit' : 'Add'} Deduction Type</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3' controlId='exampleForm.ControlInput1'>
            <Form.Label>Name</Form.Label>
            <Form.Control
              type='text'
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className='mb-3' controlId='inputcolor'>
            <Form.Label>Type</Form.Label>
            <Form.Select
              name='deductionType'
              value={formData.deductionType}
              onChange={(e) => handleChange('deductionType', e.target.value)}
              required
            >
              {DEDUCTION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className='mb-3' controlId='exampleForm.ControlInput1'>
            <Form.Label>Value</Form.Label>
            <Form.Control
              type='number'
              value={formData.value}
              onChange={(e) => handleChange('value', e.target.value)}
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='primary'
            className='d-flex align-items-center'
            disabled={loadingCreate}
          >
            {loadingCreate && <Spinner animation='border' size='sm' />}
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
